"use client"

import type React from "react"

import { useState, useEffect, useRef } from "react"
import { motion } from "framer-motion"

interface OptimizedImageProps {
  src: string
  alt: string
  className?: string
  priority?: boolean
  onLoad?: () => void
  style?: React.CSSProperties
}

// Cache global para imagens já carregadas
const imageCache: Record<string, boolean> = {}

export default function OptimizedImage({
  src,
  alt,
  className = "",
  priority = false,
  onLoad,
  style,
}: OptimizedImageProps) {
  const [isLoaded, setIsLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(!priority)
  const [currentSrc, setCurrentSrc] = useState(src)
  const imageRef = useRef<HTMLImageElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  // Gerar uma versão de baixa qualidade para placeholder
  const getLowQualitySrc = (url: string) => {
    if (url.includes("?")) {
      return `${url}&w=20&q=10&blur=10`
    }
    return `${url}?w=20&q=10&blur=10`
  }

  useEffect(() => {
    // Se a imagem já estiver em cache ou for prioritária, carregar imediatamente
    if (priority || imageCache[src]) {
      setIsLoaded(true)
      setIsLoading(false)
      if (onLoad) onLoad()
      return
    }

    // Configurar Intersection Observer para lazy loading
    if (!observerRef.current && imageRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Quando o elemento estiver visível, carregar a imagem
              const img = new Image()
              img.onload = () => {
                setIsLoaded(true)
                setIsLoading(false)
                imageCache[src] = true
                if (onLoad) onLoad()
              }
              img.src = src

              observerRef.current?.disconnect()
            }
          })
        },
        {
          rootMargin: "200px", // Começar a carregar quando estiver a 200px de distância
        },
      )

      observerRef.current.observe(imageRef.current)
    }

    return () => {
      observerRef.current?.disconnect()
    }
  }, [src, priority, onLoad])

  return (
    <div className={`relative overflow-hidden ${className}`} style={style}>
      {/* Imagem de baixa qualidade/placeholder */}
      {isLoading && (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat blur-md scale-105"
          style={{ backgroundImage: `url(${getLowQualitySrc(src)})` }}
        />
      )}

      {/* Imagem principal */}
      <motion.img
        ref={imageRef}
        src={currentSrc}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-300 ${isLoaded ? "opacity-100" : "opacity-0"}`}
        initial={false}
        animate={{ opacity: isLoaded ? 1 : 0 }}
        onLoad={() => {
          setIsLoaded(true)
          setIsLoading(false)
          imageCache[src] = true
          if (onLoad) onLoad()
        }}
        onError={() => {
          if (currentSrc !== "/placeholder.svg") {
            setCurrentSrc("/placeholder.svg")
            setIsLoaded(true)
            setIsLoading(false)
          }
        }}
      />

      {/* Efeito de carregamento */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 border-3 border-hotel-gold/30 border-t-hotel-gold rounded-full animate-spin" />
        </div>
      )}
    </div>
  )
}
